'use client'

import { useMemo } from 'react'

import { useInstrumentList } from '@/service/instrument.hook'
import type { Exchange, Instrument } from '@/types/generated/common/types'

/**
 * Returns a memoized symbol→Instrument Map for a single exchange. Callers resolve
 * their own `exchange` (e.g. via `useCardAccountContext(cardId).tradeVenue` →
 * `useAccountPrimaryExchange`, or an already-known row/tab exchange) — this hook
 * no longer reads a single global "currently active" exchange internally, since
 * that silently mis-resolves instruments for any card/row on a different
 * exchange (see the Upbit KRW-BTC collision class of bugs this replaced).
 * For call sites that must compare data across MULTIPLE exchanges at once, use
 * `useMultiExchangeInstrumentMap()` (`hooks/use-multi-exchange-instrument-map.ts`)
 * instead — this hook only ever fetches one exchange.
 */
export function useInstrumentMap(exchange: Exchange | undefined): Map<string, Instrument> {
  const { data } = useInstrumentList(exchange as Exchange, exchange !== undefined)
  return useMemo(() => new Map((data?.instruments ?? []).map((i) => [i.symbol, i])), [data])
}

/**
 * Single-symbol lookup for a single exchange. Use when a component only needs
 * one instrument. For multiple lookups, prefer useInstrumentMap() at the parent
 * level.
 */
export function useInstrumentBySymbol(
  exchange: Exchange | undefined,
  symbol: string | undefined
): Instrument | null {
  const map = useInstrumentMap(exchange)
  return useMemo(() => (symbol ? (map.get(symbol) ?? null) : null), [map, symbol])
}
